import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedCounts, setAnimatedCounts] = useState({
    rating: 0,
    reviews: 0,
    recommendation: 0,
    customers: 0
  });
  const sectionRef = useRef(null);

  // Count up animation hook with mobile optimization
  const useCountUp = (endValue, duration = 2000) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isVisible) return;

      // Use setTimeout as fallback for mobile devices
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const frameRate = isMobile ? 30 : 60; // Lower frame rate for mobile
      const steps = Math.floor((duration / 1000) * frameRate);
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeOutQuart * endValue));

        if (currentStep >= steps) {
          clearInterval(timer);
          setCount(endValue);
        }
      }, stepDuration);

      return () => clearInterval(timer);
    }, [isVisible, endValue, duration]);

    return count;
  };

  const testimonials = [
    {
      id: 1,
      name: 'Michael Schmidt',
      location: 'Dinslaken',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      rating: 5,
      service: 'NANO-Versiegelung',
      date: '15.02.2025',
      text: `Ich hatte mein altes Auto im Juni dort zur Grundreinigung. Ich bin mehr als zufrieden. Ich habe nachgefragt, ob sie neue Sitze eingebaut haben. Innen alles wie neu und von außen habe ich mein Auto nur an den alten Beulen erkannt. Ganz tolle Arbeit! Nur zu empfehlen!`,
      vehicle: 'BMW 3er Touring'
    },
    {
      id: 2,
      name: 'Sarah Müller',
      location: 'Dinslaken',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      rating: 5,
      service: 'Innenraumaufbereitung',
      date: '12.02.2025',
      text: `Unglaublich, was aus meinem Innenraum geworden ist! Die Flecken von den Kindern sind komplett verschwunden und es riecht wieder wie neu. Izzet und sein Team sind echte Profis. Kann Ragna Detailing nur weiterempfehlen!`,
      vehicle: 'VW Tiguan'
    },
    // {
    //   id: 3,
    //   name: 'Thomas Weber',
    //   location: 'Dinslaken',
    //   avatar: 'https://randomuser.me/api/portraits/men/56.jpg',
    //   rating: 5,
    //   service: 'Lackreparatur',
    //   date: '08.02.2025',
    //   text: `Die Kratzer an meinem Audi waren nach einem Parkplatz-Unfall wirklich schlimm. Ragna Detailing hat sie so repariert, dass man nichts mehr sieht. Perfekte Farbabstimmung und saubere Arbeit. Preis-Leistung stimmt absolut.`,
    //   vehicle: 'Audi A4 Avant'
    // },
    {
      id: 4,
      name: 'Jennifer Klein',
      location: 'Dinslaken',
      avatar: 'https://randomuser.me/api/portraits/women/28.jpg',
      rating: 5,
      service: 'Komplettaufbereitung',
      date: '05.02.2025',
      text: `Mein 8 Jahre alter Mercedes sieht jetzt besser aus als beim Kauf! Die Komplettaufbereitung war jeden Euro wert. Besonders beeindruckt hat mich die Liebe zum Detail. Nizamettin hat sich wirklich Zeit genommen und alles erklärt.`,
      vehicle: 'Mercedes C-Klasse'
    },
    {
      id: 5,
      name: 'Frank Richter',
      location: 'Dinslaken',
      avatar: 'https://randomuser.me/api/portraits/men/41.jpg',
      rating: 5,
      service: 'Premium Politur',
      date: '01.02.2025',
      text: `Als Autoliebhaber bin ich sehr kritisch, aber Ragna Detailing hat meine Erwartungen übertroffen. Die Politur meines Porsches ist perfekt geworden. Der Tiefenglanz ist unglaublich und die Beratung war top. Komme definitiv wieder!`,
      vehicle: 'Porsche 911'
    },
    {
      id: 6,
      name: 'Andrea Hoffmann',
      location: 'Dinslaken',
      avatar: 'https://randomuser.me/api/portraits/women/35.jpg',
      rating: 5,
      service: 'Außenhandwäsche',
      date: '28.01.2025',
      text: `Regelmäßig bei Ragna Detailing und immer zufrieden! Die Außenhandwäsche ist gründlich und schonend. Mein Ford glänzt jedes Mal wie neu. Das Team ist freundlich und die Preise sind fair. Meine Autowäsche des Vertrauens!`,
      vehicle: 'Ford Kuga'
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [testimonials?.length, isAutoPlaying]);

  // Restart auto play after manual interaction
  useEffect(() => {
    if (isAutoPlaying) return;

    const restartTimer = setTimeout(() => {
      setIsAutoPlaying(true);
    }, 10000); // Restart after 10 seconds of no interaction

    return () => clearTimeout(restartTimer);
  }, [isAutoPlaying]);

  // Intersection Observer for scroll-triggered animations with mobile optimization
  useEffect(() => {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers - trigger animation immediately
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2, // Lower threshold for mobile
        rootMargin: '0px 0px -20px 0px' // Reduced margin for mobile
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isVisible]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
    setIsAutoPlaying(false);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < rating ? 'text-primary fill-current' : 'text-muted-foreground'}
      />
    ));
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-primary/5 via-white to-accent/5">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Icon name="MessageSquare" size={16} />
            <span className="text-sm font-medium">Kundenstimmen</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Was unsere Kunden
            <span className="block text-primary">sagen</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Über 5000 zufriedene Kunden vertrauen auf unsere Expertise. 
            Lesen Sie echte Bewertungen von echten Menschen.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="relative max-w-4xl mx-auto mb-12">
          <div className="bg-white rounded-2xl shadow-card p-6 md:p-8 lg:p-12 min-h-[500px] md:min-h-[450px]">
            {/* Navigation Arrows */}
            <button
              onClick={prevTestimonial}
              className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white shadow-input rounded-full flex items-center justify-center hover:shadow-card transition-all"
              aria-label="Vorherige Bewertung"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white shadow-input rounded-full flex items-center justify-center hover:shadow-card transition-all"
              aria-label="Nächste Bewertung"
            >
              <Icon name="ChevronRight" size={16} />
            </button>

            {/* Quote Icon */}
            <div className="text-center mb-4 md:mb-6">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Icon name="Quote" size={24} className="text-primary" />
              </div>
            </div>

            {/* Testimonial Content */}
            <div className="text-center mb-6 md:mb-8">
              <div className="flex justify-center mb-4">
                {renderStars(testimonials?.[currentTestimonial]?.rating)}
              </div>
              <blockquote className="text-base md:text-lg lg:text-xl text-foreground leading-relaxed mb-4 md:mb-6 italic">
                "{testimonials?.[currentTestimonial]?.text}"
              </blockquote>
            </div>

            {/* Customer Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full overflow-hidden">
                <Image
                  src={testimonials?.[currentTestimonial]?.avatar}
                  alt={testimonials?.[currentTestimonial]?.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-center sm:text-left">
                <h4 className="font-bold text-foreground text-base md:text-lg">
                  {testimonials?.[currentTestimonial]?.name}
                </h4>
                <p className="text-sm md:text-base text-muted-foreground">
                  {testimonials?.[currentTestimonial]?.vehicle} • {testimonials?.[currentTestimonial]?.location}
                </p>
                <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-1 sm:space-y-0 sm:space-x-2 mt-1">
                  <span className="text-xs md:text-sm text-accent font-medium">
                    {testimonials?.[currentTestimonial]?.service}
                  </span>
                  <span className="text-xs md:text-sm text-muted-foreground">
                    • {testimonials?.[currentTestimonial]?.date}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Indicators */}
          <div className="flex justify-center space-x-2 mt-6">
            {testimonials?.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentTestimonial(index);
                  setIsAutoPlaying(false);
                }}
                className={`w-5 h-5 rounded-full transition-all border-2 ${
                  index === currentTestimonial ? 'bg-primary border-primary' : 'bg-muted-foreground/30 border-gray-400'
                }`}
                aria-label={`Bewertung ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Testimonial Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-12">
          {testimonials?.map((testimonial, index) => (
            <div
              key={testimonial?.id}
              className={`bg-white rounded-xl p-4 md:p-6 shadow-card hover:shadow-lg transition-all cursor-pointer ${
                index === currentTestimonial ? 'ring-2 ring-primary ring-opacity-50' : ''
              }`}
              onClick={() => {
                setCurrentTestimonial(index);
                setIsAutoPlaying(false);
              }}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden">
                  <Image
                    src={testimonial?.avatar}
                    alt={testimonial?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground text-sm md:text-base">{testimonial?.name}</h4>
                  <p className="text-xs md:text-sm text-muted-foreground">{testimonial?.location}</p>
                </div>
              </div>
              
              <div className="flex mb-3">
                {renderStars(testimonial?.rating)}
              </div>
              
              <p className="text-muted-foreground text-xs md:text-sm line-clamp-3 mb-3">
                {testimonial?.text}
              </p>
              
              <div className="text-xs text-accent font-medium">
                {testimonial?.service} • {testimonial?.date}
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-center">
          <div className="p-4 md:p-6">
            <div className="text-2xl md:text-3xl font-bold text-primary mb-2 will-change-transform">
              {useCountUp(49) / 10}★
            </div>
            <div className="text-xs md:text-sm text-muted-foreground">Durchschnittsbewertung</div>
          </div>
          <div className="p-4 md:p-6">
            <div className="text-2xl md:text-3xl font-bold text-primary mb-2 will-change-transform">
              {useCountUp(500)}+
            </div>
            <div className="text-xs md:text-sm text-muted-foreground">Google Bewertungen</div>
          </div>
          <div className="p-4 md:p-6">
            <div className="text-2xl md:text-3xl font-bold text-primary mb-2 will-change-transform">
              {useCountUp(98)}%
            </div>
            <div className="text-xs md:text-sm text-muted-foreground">Weiterempfehlung</div>
          </div>
          <div className="p-4 md:p-6">
            <div className="text-2xl md:text-3xl font-bold text-primary mb-2 will-change-transform">
              {useCountUp(5000)}+
            </div>
            <div className="text-xs md:text-sm text-muted-foreground">Zufriedene Kunden</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;